import clsx from 'clsx';
import React from 'react';

import LazyImgixImage from '@components/LazyImgixImage';
import TimeAgo from '@components/TimeAgo';
import useEventTracking from '@hooks/useEventTracking';
import { titv_show_path } from '@routes';
import { ClickTarget, ContentType, click } from '@tracking';
import type { TITVEvent } from '@utils/backend-api/titv';

interface TITVMultistreamFeatureHeroStreamCardProps {
  className?: string;
  isFluid?: boolean;
  stream: TITVEvent;
}

const TITVMultistreamFeatureHeroStreamCard = ({
  className,
  isFluid = false,
  stream,
}: TITVMultistreamFeatureHeroStreamCardProps) => {
  const { coverUrl, datetime, guid, id, title } = stream;
  const { trackEvent, Track } = useEventTracking({
    contentType: ContentType.TITVStream,
    contentId: id,
  });

  const targetLink = titv_show_path(guid);

  return (
    <Track>
      <article
        className={clsx(
          'flex-1 border-0 border-b border-solid border-grayscale-gray px-16 pb-32 sm:px-0',
          className
        )}
      >
        <a
          onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.ArticleImage }))}
          href={targetLink}
          className={clsx('relative -mx-16 mb-16 block sm:mx-0 sm:mb-24', 'first-of-type:!mx-0', {
            'h-[calc((100vw)*9/16)] sm:h-[calc((100vw-3.5rem)*9/16)] md:h-[calc(712px*9/16)] md:min-w-[712px] lg:h-[calc(884px*9/16)] lg:min-w-[884px]':
              !isFluid,
            'aspect-[16/9] flex-shrink flex-grow text-[1px] leading-none sm:w-full lg:max-w-[884px]':
              isFluid,
          })}
        >
          <LazyImgixImage
            alt={`Episode still from ${title}`}
            className="h-auto w-full"
            src={coverUrl}
            sizes="(min-width: 480px) 600px, 400px"
            wrapperClassName="h-full w-full"
            visibleByDefault
          />
        </a>
        <a
          onClickCapture={() =>
            trackEvent(click({ clickTarget: ClickTarget.Headline, clickText: title }))
          }
          href={targetLink}
          className="text-balance"
        >
          <h3 className="m-0 text-balance font-suisse-intl text-body-md font-normal text-primary-black hover:text-primary-magenta sm:text-body-lg dark:hover:text-primary-pink">
            {title}
          </h3>
          <TimeAgo
            className="font-suisse-screen text-disclosure-sm text-grayscale-charcoal"
            date={datetime}
            datetime={datetime}
          />
        </a>
      </article>
    </Track>
  );
};

export default TITVMultistreamFeatureHeroStreamCard;
